import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useColorScheme } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { palette } from '../utils/palette';

export type ThemeMode = 'light' | 'dark' | 'system';

// Query keys
export const themeKeys = {
  all: ['theme'] as const,
  mode: () => [...themeKeys.all, 'mode'] as const,
};

const defaultColors = {
  light: {
    theme_color: palette.softViolet,
    /** Softer global background (#F9FAFB) for full-page and containers; reduces glare. */
    background: palette.softGray,
    /** Text/icon on primary buttons and dark surfaces; use for contrast. */
    onPrimary: palette.white,
    text: palette.veryDarkBlue,
    primary: palette.brightViolet,
    secondary: palette.darkGrayBlue,
    border: palette.veryLightGray,
    borderLight: palette.lightGray,
    card: palette.white,
    cardText: palette.veryDarkBlue,
    authBackground: palette.lightGrayViolet,
    errorBackground: palette.lightRed,
    errorText: palette.brightRed,
    success: palette.cyanLimeGreen,
    subscription: palette.vividOrange,
    billing: palette.brightMatteRed,
    supportText: palette.brightBlue,
    helpCenterText: palette.lightOrange,
    cool: palette.vividBlue,
    defaultUndertone: palette.mediumGray3,
    subscriptionAndBillingIcon: palette.strongCyan,
    virtualDrapeGradient: palette.stongCyanLimeGreen,
    smartWardrobeGradient: palette.vividLightOrange,
    weatherGradient1: palette.verySoftBlue,
    weatherGradient2: palette.lightGrayishBlue,
    fabGradientPurple: palette.verySoftViolet,
    makeupItem: palette.softPink,
    stars: palette.vividYellow,
    linearGradient1: palette.softBlue,
    linearGradient2: palette.darkModerateViolet,
    linearGradient3: palette.darkCyanLimeGreen,
    linearGradient4: palette.lightVividOrange,
    hoverBackground1: palette.softOrange,
    hoverBackground2: palette.brightCyanLimeGreen,
    appGradient1: palette.gradientPink,
    appGradient2: palette.gradientBlue,
    /** Main content + top bar: soft off-white (#f4f5fa) for reduced glare and a single-surface feel. */
    panelBackground: palette.panelBackground,
    statusBar: 'dark-content' as const,
  },
  dark: {
    theme_color: palette.softViolet,
    background: palette.lightBlack,
    onPrimary: palette.white,
    text: palette.softGray,
    primary: palette.verySoftViolet,
    secondary: palette.mediumGray,
    border: palette.darkGray,
    borderLight: palette.mediumGray2,
    card: palette.veryDarkBlue,
    cardText: palette.softGray,
    authBackground: palette.lightGrayViolet,
    errorBackground: palette.lightRed,
    errorText: palette.brightRed,
    success: palette.cyanLimeGreen,
    subscription: palette.vividOrange,
    billing: palette.brightMatteRed,
    supportText: palette.brightBlue,
    helpCenterText: palette.lightOrange,
    cool: palette.vividBlue,
    defaultUndertone: palette.mediumGray3,
    subscriptionAndBillingIcon: palette.strongCyan,
    virtualDrapeGradient: palette.stongCyanLimeGreen,
    smartWardrobeGradient: palette.vividLightOrange,
    weatherGradient1: palette.verySoftBlue,
    weatherGradient2: palette.lightGrayishBlue,
    fabGradientPurple: palette.verySoftViolet,
    makeupItem: palette.softPink,
    stars: palette.vividYellow,
    linearGradient1: palette.softBlue,
    linearGradient2: palette.darkModerateViolet,
    linearGradient3: palette.darkCyanLimeGreen,
    linearGradient4: palette.lightVividOrange,
    hoverBackground1: palette.softOrange,
    hoverBackground2: palette.brightCyanLimeGreen,
    appGradient1: palette.gradientPink,
    appGradient2: palette.gradientBlue,
    panelBackground: palette.panelBackground,
    statusBar: 'light-content' as const,
  },
};

// Hook to get theme mode
const useThemeMode = () => {
  return useQuery({
    queryKey: themeKeys.mode(),
    queryFn: async () => {
      try {
        const savedTheme = await AsyncStorage.getItem('@theme');
        return (savedTheme as ThemeMode) || 'system';
      } catch (error) {
        console.error('Failed to load theme', error);
        return 'system' as ThemeMode;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity, // Keep in cache indefinitely
  });
};

// Main theme hook
export const useTheme = () => {
  const queryClient = useQueryClient();
  const deviceTheme = useColorScheme();
  const { data: theme = 'system' } = useThemeMode();

  // Determine if dark mode should be active
  const isDarkMode =
    theme === 'dark' || (theme === 'system' && deviceTheme === 'dark');

  // Get the appropriate colors based on the current theme
  const colors = defaultColors.light; // Always use light for now (as per original implementation)

  // Set theme mutation
  const setThemeMutation = useMutation({
    mutationFn: async (newTheme: ThemeMode) => {
      try {
        await AsyncStorage.setItem('@theme', newTheme);
        return newTheme;
      } catch (error) {
        console.error('Failed to save theme', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(themeKeys.mode(), data);
    },
  });

  return {
    theme,
    isDarkMode,
    setTheme: setThemeMutation.mutateAsync,
    colors,
  };
};
